import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
    Trophy, Users, Calendar, MapPin, Clock, Edit, Star,
    CheckCircle, XCircle, Play, Pause, Award, Target,
    Plus, Trash2, ChevronRight, Shield, Zap, Goal
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sport-specific scorer labels
const getScorerLabel = (sport) => {
    const labels = {
        'Football': 'Goals',
        'Basketball': 'Points',
        'Hockey': 'Goals',
        'Volleyball': 'Points',
        'Rugby': 'Tries',
        'Handball': 'Goals',
        'Tennis': 'Games Won',
        'Padel': 'Games Won',
        'Netball': 'Goals',
        'default': 'Points'
    };
    return labels[sport] || labels.default;
};

const MatchesTab = ({ tournaments, token, user }) => {
    const [loading, setLoading] = useState(true);
    const [matches, setMatches] = useState([]);
    const [selectedTournament, setSelectedTournament] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');
    
    // Dialog states
    const [showStatsDialog, setShowStatsDialog] = useState(false);
    const [editingMatch, setEditingMatch] = useState(null);
    const [matchPlayers, setMatchPlayers] = useState({ home_team: { name: '', players: [] }, away_team: { name: '', players: [] } });
    const [loadingPlayers, setLoadingPlayers] = useState(false);
    
    // Form state for comprehensive stats
    const [statsForm, setStatsForm] = useState({
        home_score: 0,
        away_score: 0,
        scorers: [],
        home_mvp: { player_name: '', team: '', category: 'team_mvp', notes: '' },
        away_mvp: { player_name: '', team: '', category: 'team_mvp', notes: '' },
        match_mvp: { player_name: '', team: '', category: 'overall', notes: '' },
        offensive_mvp: { player_name: '', team: '', category: 'offensive', notes: '' },
        defensive_mvp: { player_name: '', team: '', category: 'defensive', notes: '' }
    });

    const api = axios.create({
        baseURL: `${API_URL}/api`,
        headers: { Authorization: `Bearer ${token}` }
    });

    useEffect(() => {
        loadMatches();
    }, [selectedTournament]);

    const loadMatches = async () => {
        setLoading(true);
        try {
            let allMatches = [];
            
            if (selectedTournament === 'all') {
                for (const t of tournaments || []) {
                    try {
                        const res = await api.get(`/matches/tournament/${t.id}/matches`);
                        const tournamentMatches = (res.data || []).map(m => ({
                            ...m,
                            tournament_name: t.name,
                            sport: t.sport
                        }));
                        allMatches = [...allMatches, ...tournamentMatches];
                    } catch (err) {
                        // Tournament might not have matches
                    }
                }
            } else {
                const tournament = tournaments?.find(t => t.id === selectedTournament);
                const res = await api.get(`/matches/tournament/${selectedTournament}/matches`);
                allMatches = (res.data || []).map(m => ({
                    ...m,
                    tournament_name: tournament?.name || 'Unknown',
                    sport: tournament?.sport
                }));
            }
            
            allMatches.sort((a, b) => {
                const dateA = new Date(a.scheduled_date || a.date);
                const dateB = new Date(b.scheduled_date || b.date);
                return dateA - dateB;
            });
            
            setMatches(allMatches);
        } catch (error) {
            console.error('Failed to load matches:', error);
            setMatches([]);
        } finally {
            setLoading(false);
        }
    };

    const loadMatchPlayers = async (match) => {
        setLoadingPlayers(true);
        try {
            const res = await api.get(`/matches/${match.id}/players`);
            setMatchPlayers(res.data);
        } catch (error) {
            console.error('Failed to load players:', error);
            // Fallback to manual entry
            setMatchPlayers({
                home_team: { name: match.home_team || 'Home Team', players: [] },
                away_team: { name: match.away_team || 'Away Team', players: [] }
            });
        } finally {
            setLoadingPlayers(false);
        }
    };

    const openStatsDialog = async (match) => {
        setEditingMatch(match);
        
        // Initialize form with existing data
        setStatsForm({
            home_score: match.score1 || match.home_score || 0,
            away_score: match.score2 || match.away_score || 0,
            scorers: match.scorers || [],
            home_mvp: match.mvps?.home_mvp || { player_name: '', team: match.home_team || '', category: 'team_mvp', notes: '' },
            away_mvp: match.mvps?.away_mvp || { player_name: '', team: match.away_team || '', category: 'team_mvp', notes: '' },
            match_mvp: match.mvps?.match_mvp || match.mvp || { player_name: '', team: '', category: 'overall', notes: '' },
            offensive_mvp: match.mvps?.offensive_mvp || { player_name: '', team: '', category: 'offensive', notes: '' },
            defensive_mvp: match.mvps?.defensive_mvp || { player_name: '', team: '', category: 'defensive', notes: '' }
        });
        
        await loadMatchPlayers(match);
        setShowStatsDialog(true);
    };

    const handleSaveStats = async () => {
        if (!editingMatch) return;
        
        try {
            // Filter out empty MVPs
            const payload = {
                home_score: parseInt(statsForm.home_score) || 0,
                away_score: parseInt(statsForm.away_score) || 0,
                scorers: statsForm.scorers.filter(s => s.player_name),
            };
            
            if (statsForm.home_mvp?.player_name) payload.home_mvp = statsForm.home_mvp;
            if (statsForm.away_mvp?.player_name) payload.away_mvp = statsForm.away_mvp;
            if (statsForm.match_mvp?.player_name) payload.match_mvp = statsForm.match_mvp;
            if (statsForm.offensive_mvp?.player_name) payload.offensive_mvp = statsForm.offensive_mvp;
            if (statsForm.defensive_mvp?.player_name) payload.defensive_mvp = statsForm.defensive_mvp;
            
            await api.put(`/matches/${editingMatch.id}/full-stats`, payload);
            toast.success('Match statistics saved successfully!');
            setShowStatsDialog(false);
            setEditingMatch(null);
            loadMatches();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save statistics');
        }
    };

    const handleCompleteMatch = async (advanceWinner = true) => {
        if (!editingMatch) return;
        
        const homeScore = parseInt(statsForm.home_score) || 0;
        const awayScore = parseInt(statsForm.away_score) || 0;
        
        // Check for draw in knockout matches
        if (homeScore === awayScore && advanceWinner) {
            toast.error('Match is a draw. Please update scores or select a penalty winner.');
            return;
        }
        
        try {
            // First save stats
            const statsPayload = {
                home_score: homeScore,
                away_score: awayScore,
                scorers: statsForm.scorers.filter(s => s.player_name),
            };
            
            if (statsForm.home_mvp?.player_name) statsPayload.home_mvp = statsForm.home_mvp;
            if (statsForm.away_mvp?.player_name) statsPayload.away_mvp = statsForm.away_mvp;
            if (statsForm.match_mvp?.player_name) statsPayload.match_mvp = statsForm.match_mvp;
            if (statsForm.offensive_mvp?.player_name) statsPayload.offensive_mvp = statsForm.offensive_mvp;
            if (statsForm.defensive_mvp?.player_name) statsPayload.defensive_mvp = statsForm.defensive_mvp;
            
            await api.put(`/matches/${editingMatch.id}/full-stats`, statsPayload);
            
            // Then complete the match and advance winner
            const completePayload = {
                home_score: homeScore,
                away_score: awayScore,
                advance_winner: advanceWinner
            };
            
            const response = await api.put(`/matches/${editingMatch.id}/complete`, completePayload);
            
            if (response.data.advancement?.advanced) {
                toast.success(
                    `Match completed! ${response.data.winner} advances to ${response.data.advancement.next_round}`,
                    { duration: 5000 }
                );
            } else {
                toast.success(`Match completed! Winner: ${response.data.winner}`);
            }
            
            setShowStatsDialog(false);
            setEditingMatch(null);
            loadMatches();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to complete match');
        }
    };

    const addScorer = () => {
        setStatsForm(prev => ({
            ...prev,
            scorers: [...prev.scorers, { player_name: '', team: '', count: 1, minute: null, assist_by: '' }]
        }));
    };

    const updateScorer = (index, field, value) => {
        setStatsForm(prev => {
            const newScorers = [...prev.scorers];
            newScorers[index] = { ...newScorers[index], [field]: value };
            return { ...prev, scorers: newScorers };
        });
    };

    const removeScorer = (index) => {
        setStatsForm(prev => ({
            ...prev,
            scorers: prev.scorers.filter((_, i) => i !== index)
        }));
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            scheduled: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Calendar },
            live: { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: Play },
            completed: { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: CheckCircle },
            cancelled: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle }
        };
        const config = statusConfig[status] || statusConfig.scheduled;
        const Icon = config.icon;
        
        return (
            <span className={`px-2 py-1 text-xs border ${config.color} flex items-center gap-1 w-fit`}>
                <Icon className="w-3 h-3" />
                {status?.toUpperCase()}
            </span>
        );
    };

    // Get all players from both teams for dropdown
    const getAllPlayers = () => {
        const homePlayers = matchPlayers.home_team?.players?.map(p => ({
            ...p,
            team: matchPlayers.home_team.name,
            displayName: p.jersey_number ? `#${p.jersey_number} ${p.name}` : p.name
        })) || [];
        const awayPlayers = matchPlayers.away_team?.players?.map(p => ({
            ...p,
            team: matchPlayers.away_team.name,
            displayName: p.jersey_number ? `#${p.jersey_number} ${p.name}` : p.name
        })) || [];
        return [...homePlayers, ...awayPlayers];
    };

    const filteredMatches = matches.filter(m => {
        if (selectedStatus !== 'all' && m.status !== selectedStatus) return false;
        return true;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                    <h2 className="font-heading font-bold text-xl uppercase flex items-center gap-2">
                        <Trophy className="w-6 h-6 text-primary" />
                        Match Management
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        {user?.role === 'official_referee' 
                            ? 'Update scores, scorers, and MVPs for your assigned matches'
                            : 'View and manage tournament matches'}
                    </p>
                </div>
                
                <div className="flex flex-wrap items-center gap-2">
                    <Select value={selectedTournament} onValueChange={setSelectedTournament}>
                        <SelectTrigger className="w-[200px] bg-[#0a1628] border-white/10">
                            <SelectValue placeholder="All Tournaments" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0d2847] border-white/10">
                            <SelectItem value="all">All Tournaments</SelectItem>
                            {tournaments?.map(t => (
                                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    
                    <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                        <SelectTrigger className="w-[150px] bg-[#0a1628] border-white/10">
                            <SelectValue placeholder="All Status" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0d2847] border-white/10">
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="scheduled">Scheduled</SelectItem>
                            <SelectItem value="live">Live</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Matches List */}
            <div className="space-y-4">
                {filteredMatches.length > 0 ? (
                    filteredMatches.map((match) => (
                        <div 
                            key={match.id} 
                            className="bg-[#0d2847] border border-white/5 p-4 hover:border-primary/30 transition-colors"
                            data-testid={`match-${match.id}`}
                        >
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                {/* Match Info */}
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        {getStatusBadge(match.status)}
                                        <span className="text-xs text-muted-foreground">{match.round || 'Match'}</span>
                                        <span className="text-xs text-cyan-400">{match.tournament_name}</span>
                                        {match.sport && <span className="text-xs text-orange-400">({match.sport})</span>}
                                    </div>
                                    
                                    {/* Teams and Score */}
                                    <div className="flex items-center gap-4">
                                        <div className="flex-1 text-right">
                                            <p className="font-bold text-lg">{match.home_team || match.team1 || 'Team A'}</p>
                                        </div>
                                        
                                        <div className="flex items-center gap-2 px-4 py-2 bg-[#0a1628] border border-white/10">
                                            <span className="text-2xl font-bold text-green-400">
                                                {match.score1 || match.home_score || 0}
                                            </span>
                                            <span className="text-muted-foreground">-</span>
                                            <span className="text-2xl font-bold text-red-400">
                                                {match.score2 || match.away_score || 0}
                                            </span>
                                        </div>
                                        
                                        <div className="flex-1">
                                            <p className="font-bold text-lg">{match.away_team || match.team2 || 'Team B'}</p>
                                        </div>
                                    </div>
                                    
                                    {/* Match Details */}
                                    <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {match.scheduled_date || match.date || 'TBD'}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {match.scheduled_time || match.time || 'TBD'}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <MapPin className="w-3 h-3" />
                                            {match.venue || 'TBD'}
                                        </span>
                                    </div>
                                    
                                    {/* Scorers */}
                                    {match.scorers && match.scorers.length > 0 && (
                                        <div className="mt-3 flex flex-wrap items-center gap-2">
                                            <Goal className="w-4 h-4 text-green-400" />
                                            {match.scorers.map((s, idx) => (
                                                <span key={idx} className="text-xs bg-green-500/10 px-2 py-1 border border-green-500/30">
                                                    {s.player_name} ({s.count}){s.minute ? ` ${s.minute}'` : ''}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* MVPs Display */}
                                    <div className="mt-3 flex flex-wrap items-center gap-3">
                                        {match.mvps?.home_mvp && (
                                            <span className="flex items-center gap-1 text-xs bg-blue-500/10 px-2 py-1 border border-blue-500/30">
                                                <Star className="w-3 h-3 text-blue-400" />
                                                Home MVP: {match.mvps.home_mvp.player_name}
                                            </span>
                                        )}
                                        {match.mvps?.away_mvp && (
                                            <span className="flex items-center gap-1 text-xs bg-red-500/10 px-2 py-1 border border-red-500/30">
                                                <Star className="w-3 h-3 text-red-400" />
                                                Away MVP: {match.mvps.away_mvp.player_name}
                                            </span>
                                        )}
                                        {(match.mvps?.match_mvp || match.mvp) && (
                                            <span className="flex items-center gap-1 text-xs bg-yellow-500/10 px-2 py-1 border border-yellow-500/30">
                                                <Trophy className="w-3 h-3 text-yellow-400" />
                                                Match MVP: {match.mvps?.match_mvp?.player_name || match.mvp?.player_name}
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* Winner Display for Completed Matches */}
                                    {match.status === 'completed' && match.winner && (
                                        <div className="mt-3 p-2 bg-green-500/10 border border-green-500/30 flex items-center gap-2">
                                            <Trophy className="w-4 h-4 text-green-400" />
                                            <span className="text-sm font-semibold text-green-400">
                                                Winner: {match.winner}
                                            </span>
                                            {match.advancement?.advanced && (
                                                <span className="text-xs text-muted-foreground ml-2">
                                                    → Advanced to {match.advancement.next_round}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Actions */}
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openStatsDialog(match)}
                                        className="border-primary/30 text-primary hover:bg-primary/10"
                                        data-testid={`update-stats-${match.id}`}
                                    >
                                        <Edit className="w-4 h-4 mr-1" />
                                        Update Stats
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="bg-[#0d2847] border border-white/5 p-12 text-center">
                        <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No matches found</p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {selectedTournament === 'all' 
                                ? 'Select a tournament to view its matches'
                                : 'This tournament has no matches scheduled yet'}
                        </p>
                    </div>
                )}
            </div>

            {/* Update Stats Dialog */}
            <Dialog open={showStatsDialog} onOpenChange={setShowStatsDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-bold text-xl uppercase flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-primary" />
                            Update Match Statistics
                        </DialogTitle>
                    </DialogHeader>
                    
                    {editingMatch && (
                        <div className="space-y-6 py-4">
                            {/* Match Header */}
                            <div className="text-center p-4 bg-[#0a1628] border border-white/10">
                                <p className="text-sm text-muted-foreground">{editingMatch.round}</p>
                                <p className="font-bold text-lg">
                                    {editingMatch.home_team} vs {editingMatch.away_team}
                                </p>
                                {editingMatch.sport && (
                                    <p className="text-xs text-cyan-400">{editingMatch.sport}</p>
                                )}
                            </div>
                            
                            <Tabs defaultValue="scores" className="w-full">
                                <TabsList className="grid w-full grid-cols-3 bg-[#0a1628]">
                                    <TabsTrigger value="scores" className="data-[state=active]:bg-primary">
                                        <Target className="w-4 h-4 mr-2" />
                                        Scores
                                    </TabsTrigger>
                                    <TabsTrigger value="scorers" className="data-[state=active]:bg-green-600">
                                        <Goal className="w-4 h-4 mr-2" />
                                        {getScorerLabel(editingMatch.sport)}
                                    </TabsTrigger>
                                    <TabsTrigger value="mvps" className="data-[state=active]:bg-yellow-600">
                                        <Award className="w-4 h-4 mr-2" />
                                        MVPs
                                    </TabsTrigger>
                                </TabsList>
                                
                                {/* Scores Tab */}
                                <TabsContent value="scores" className="space-y-4 mt-4">
                                    <div className="grid grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground flex items-center gap-2">
                                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                                {matchPlayers.home_team?.name || editingMatch.home_team}
                                            </Label>
                                            <Input
                                                type="number"
                                                min="0"
                                                value={statsForm.home_score}
                                                onChange={(e) => setStatsForm({...statsForm, home_score: e.target.value})}
                                                className="bg-[#0a1628] border-white/10 text-center text-3xl font-bold h-20"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-xs text-muted-foreground flex items-center gap-2">
                                                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                                {matchPlayers.away_team?.name || editingMatch.away_team}
                                            </Label>
                                            <Input
                                                type="number"
                                                min="0"
                                                value={statsForm.away_score}
                                                onChange={(e) => setStatsForm({...statsForm, away_score: e.target.value})}
                                                className="bg-[#0a1628] border-white/10 text-center text-3xl font-bold h-20"
                                            />
                                        </div>
                                    </div>
                                </TabsContent>
                                
                                {/* Scorers Tab */}
                                <TabsContent value="scorers" className="space-y-4 mt-4">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-sm font-semibold">
                                            {getScorerLabel(editingMatch.sport)} Scorers
                                        </Label>
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={addScorer}
                                            className="bg-green-600 hover:bg-green-700"
                                        >
                                            <Plus className="w-4 h-4 mr-1" />
                                            Add Scorer
                                        </Button>
                                    </div>
                                    
                                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                                        {statsForm.scorers.map((scorer, index) => (
                                            <div key={index} className="grid grid-cols-12 gap-2 p-3 bg-[#0a1628] border border-white/10">
                                                {/* Team Select */}
                                                <div className="col-span-3">
                                                    <Label className="text-xs text-muted-foreground">Team</Label>
                                                    <Select
                                                        value={scorer.team || undefined}
                                                        onValueChange={(v) => updateScorer(index, 'team', v)}
                                                    >
                                                        <SelectTrigger className="bg-[#0d2847] border-white/10 h-9 text-xs">
                                                            <SelectValue placeholder="Select team" />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-[#0d2847] border-white/10">
                                                            <SelectItem value={matchPlayers.home_team?.name || editingMatch.home_team}>
                                                                {matchPlayers.home_team?.name || editingMatch.home_team}
                                                            </SelectItem>
                                                            <SelectItem value={matchPlayers.away_team?.name || editingMatch.away_team}>
                                                                {matchPlayers.away_team?.name || editingMatch.away_team}
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                
                                                {/* Player Select */}
                                                <div className="col-span-4">
                                                    <Label className="text-xs text-muted-foreground">Player</Label>
                                                    {getAllPlayers().length > 0 ? (
                                                        <Select
                                                            value={scorer.player_name || undefined}
                                                            onValueChange={(v) => {
                                                                const player = getAllPlayers().find(p => p.name === v);
                                                                updateScorer(index, 'player_name', v);
                                                                if (player) updateScorer(index, 'team', player.team);
                                                            }}
                                                        >
                                                            <SelectTrigger className="bg-[#0d2847] border-white/10 h-9 text-xs">
                                                                <SelectValue placeholder="Select player" />
                                                            </SelectTrigger>
                                                            <SelectContent className="bg-[#0d2847] border-white/10 max-h-[200px]">
                                                                {getAllPlayers().map((p, i) => (
                                                                    <SelectItem key={i} value={p.name}>
                                                                        {p.displayName} ({p.team})
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    ) : (
                                                        <Input
                                                            value={scorer.player_name}
                                                            onChange={(e) => updateScorer(index, 'player_name', e.target.value)}
                                                            placeholder="Player name"
                                                            className="bg-[#0d2847] border-white/10 h-9 text-xs"
                                                        />
                                                    )}
                                                </div>
                                                
                                                {/* Count */}
                                                <div className="col-span-2">
                                                    <Label className="text-xs text-muted-foreground">Count</Label>
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        value={scorer.count}
                                                        onChange={(e) => updateScorer(index, 'count', parseInt(e.target.value) || 1)}
                                                        className="bg-[#0d2847] border-white/10 h-9 text-xs"
                                                    />
                                                </div>
                                                
                                                {/* Minute */}
                                                <div className="col-span-2">
                                                    <Label className="text-xs text-muted-foreground">Minute</Label>
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        value={scorer.minute || ''}
                                                        onChange={(e) => updateScorer(index, 'minute', e.target.value ? parseInt(e.target.value) : null)}
                                                        placeholder="'"
                                                        className="bg-[#0d2847] border-white/10 h-9 text-xs"
                                                    />
                                                </div>
                                                
                                                {/* Delete */}
                                                <div className="col-span-1 flex items-end">
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeScorer(index)}
                                                        className="h-9 w-9 p-0 text-red-400 hover:bg-red-500/10"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                        
                                        {statsForm.scorers.length === 0 && (
                                            <p className="text-center text-muted-foreground py-8">
                                                No scorers added yet. Click "Add Scorer" to record {getScorerLabel(editingMatch.sport).toLowerCase()}.
                                            </p>
                                        )}
                                    </div>
                                </TabsContent>
                                
                                {/* MVPs Tab */}
                                <TabsContent value="mvps" className="space-y-6 mt-4">
                                    {/* Team MVPs */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Home Team MVP */}
                                        <div className="p-4 bg-[#0a1628] border border-blue-500/30">
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                                <Label className="font-semibold text-blue-400">
                                                    {matchPlayers.home_team?.name || editingMatch.home_team} MVP
                                                </Label>
                                            </div>
                                            {getAllPlayers().filter(p => p.team === (matchPlayers.home_team?.name || editingMatch.home_team)).length > 0 ? (
                                                <Select
                                                    value={statsForm.home_mvp?.player_name || undefined}
                                                    onValueChange={(v) => setStatsForm({
                                                        ...statsForm,
                                                        home_mvp: { ...statsForm.home_mvp, player_name: v, team: matchPlayers.home_team?.name || editingMatch.home_team }
                                                    })}
                                                >
                                                    <SelectTrigger className="bg-[#0d2847] border-white/10">
                                                        <SelectValue placeholder="Select player" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                                        {getAllPlayers().filter(p => p.team === (matchPlayers.home_team?.name || editingMatch.home_team)).map((p, i) => (
                                                            <SelectItem key={i} value={p.name}>{p.displayName}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            ) : (
                                                <Input
                                                    value={statsForm.home_mvp?.player_name || ''}
                                                    onChange={(e) => setStatsForm({
                                                        ...statsForm,
                                                        home_mvp: { ...statsForm.home_mvp, player_name: e.target.value, team: matchPlayers.home_team?.name || editingMatch.home_team }
                                                    })}
                                                    placeholder="Enter player name"
                                                    className="bg-[#0d2847] border-white/10"
                                                />
                                            )}
                                        </div>
                                        
                                        {/* Away Team MVP */}
                                        <div className="p-4 bg-[#0a1628] border border-red-500/30">
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                                <Label className="font-semibold text-red-400">
                                                    {matchPlayers.away_team?.name || editingMatch.away_team} MVP
                                                </Label>
                                            </div>
                                            {getAllPlayers().filter(p => p.team === (matchPlayers.away_team?.name || editingMatch.away_team)).length > 0 ? (
                                                <Select
                                                    value={statsForm.away_mvp?.player_name || undefined}
                                                    onValueChange={(v) => setStatsForm({
                                                        ...statsForm,
                                                        away_mvp: { ...statsForm.away_mvp, player_name: v, team: matchPlayers.away_team?.name || editingMatch.away_team }
                                                    })}
                                                >
                                                    <SelectTrigger className="bg-[#0d2847] border-white/10">
                                                        <SelectValue placeholder="Select player" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                                        {getAllPlayers().filter(p => p.team === (matchPlayers.away_team?.name || editingMatch.away_team)).map((p, i) => (
                                                            <SelectItem key={i} value={p.name}>{p.displayName}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            ) : (
                                                <Input
                                                    value={statsForm.away_mvp?.player_name || ''}
                                                    onChange={(e) => setStatsForm({
                                                        ...statsForm,
                                                        away_mvp: { ...statsForm.away_mvp, player_name: e.target.value, team: matchPlayers.away_team?.name || editingMatch.away_team }
                                                    })}
                                                    placeholder="Enter player name"
                                                    className="bg-[#0d2847] border-white/10"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Match MVP (Overall) */}
                                    <div className="p-4 bg-[#0a1628] border border-yellow-500/30">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Trophy className="w-4 h-4 text-yellow-400" />
                                            <Label className="font-semibold text-yellow-400">Match MVP (Overall)</Label>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label className="text-xs text-muted-foreground">Team</Label>
                                                <Select
                                                    value={statsForm.match_mvp?.team || undefined}
                                                    onValueChange={(v) => setStatsForm({
                                                        ...statsForm,
                                                        match_mvp: { ...statsForm.match_mvp, team: v }
                                                    })}
                                                >
                                                    <SelectTrigger className="bg-[#0d2847] border-white/10">
                                                        <SelectValue placeholder="Select team" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                                        <SelectItem value={matchPlayers.home_team?.name || editingMatch.home_team}>
                                                            {matchPlayers.home_team?.name || editingMatch.home_team}
                                                        </SelectItem>
                                                        <SelectItem value={matchPlayers.away_team?.name || editingMatch.away_team}>
                                                            {matchPlayers.away_team?.name || editingMatch.away_team}
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div>
                                                <Label className="text-xs text-muted-foreground">Player</Label>
                                                {getAllPlayers().filter(p => !statsForm.match_mvp?.team || p.team === statsForm.match_mvp.team).length > 0 ? (
                                                    <Select
                                                        value={statsForm.match_mvp?.player_name || undefined}
                                                        onValueChange={(v) => {
                                                            const player = getAllPlayers().find(p => p.name === v);
                                                            setStatsForm({
                                                                ...statsForm,
                                                                match_mvp: { ...statsForm.match_mvp, player_name: v, team: player?.team || statsForm.match_mvp?.team }
                                                            });
                                                        }}
                                                    >
                                                        <SelectTrigger className="bg-[#0d2847] border-white/10">
                                                            <SelectValue placeholder="Select player" />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-[#0d2847] border-white/10">
                                                            {getAllPlayers().filter(p => !statsForm.match_mvp?.team || p.team === statsForm.match_mvp.team).map((p, i) => (
                                                                <SelectItem key={i} value={p.name}>{p.displayName}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                ) : (
                                                    <Input
                                                        value={statsForm.match_mvp?.player_name || ''}
                                                        onChange={(e) => setStatsForm({
                                                            ...statsForm,
                                                            match_mvp: { ...statsForm.match_mvp, player_name: e.target.value }
                                                        })}
                                                        placeholder="Enter player name"
                                                        className="bg-[#0d2847] border-white/10"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Offensive & Defensive MVPs */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Offensive MVP */}
                                        <div className="p-4 bg-[#0a1628] border border-orange-500/30">
                                            <div className="flex items-center gap-2 mb-3">
                                                <Zap className="w-4 h-4 text-orange-400" />
                                                <Label className="font-semibold text-orange-400">Offensive MVP</Label>
                                            </div>
                                            <div className="space-y-2">
                                                <Select
                                                    value={statsForm.offensive_mvp?.team || undefined}
                                                    onValueChange={(v) => setStatsForm({
                                                        ...statsForm,
                                                        offensive_mvp: { ...statsForm.offensive_mvp, team: v }
                                                    })}
                                                >
                                                    <SelectTrigger className="bg-[#0d2847] border-white/10">
                                                        <SelectValue placeholder="Select team" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                                        <SelectItem value={matchPlayers.home_team?.name || editingMatch.home_team}>
                                                            {matchPlayers.home_team?.name || editingMatch.home_team}
                                                        </SelectItem>
                                                        <SelectItem value={matchPlayers.away_team?.name || editingMatch.away_team}>
                                                            {matchPlayers.away_team?.name || editingMatch.away_team}
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                {getAllPlayers().filter(p => !statsForm.offensive_mvp?.team || p.team === statsForm.offensive_mvp.team).length > 0 ? (
                                                    <Select
                                                        value={statsForm.offensive_mvp?.player_name || undefined}
                                                        onValueChange={(v) => {
                                                            const player = getAllPlayers().find(p => p.name === v);
                                                            setStatsForm({
                                                                ...statsForm,
                                                                offensive_mvp: { ...statsForm.offensive_mvp, player_name: v, team: player?.team || statsForm.offensive_mvp?.team }
                                                            });
                                                        }}
                                                    >
                                                        <SelectTrigger className="bg-[#0d2847] border-white/10">
                                                            <SelectValue placeholder="Select player" />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-[#0d2847] border-white/10">
                                                            {getAllPlayers().filter(p => !statsForm.offensive_mvp?.team || p.team === statsForm.offensive_mvp.team).map((p, i) => (
                                                                <SelectItem key={i} value={p.name}>{p.displayName}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                ) : (
                                                    <Input
                                                        value={statsForm.offensive_mvp?.player_name || ''}
                                                        onChange={(e) => setStatsForm({
                                                            ...statsForm,
                                                            offensive_mvp: { ...statsForm.offensive_mvp, player_name: e.target.value }
                                                        })}
                                                        placeholder="Enter player name"
                                                        className="bg-[#0d2847] border-white/10"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Defensive MVP */}
                                        <div className="p-4 bg-[#0a1628] border border-cyan-500/30">
                                            <div className="flex items-center gap-2 mb-3">
                                                <Shield className="w-4 h-4 text-cyan-400" />
                                                <Label className="font-semibold text-cyan-400">Defensive MVP</Label>
                                            </div>
                                            <div className="space-y-2">
                                                <Select
                                                    value={statsForm.defensive_mvp?.team || undefined}
                                                    onValueChange={(v) => setStatsForm({
                                                        ...statsForm,
                                                        defensive_mvp: { ...statsForm.defensive_mvp, team: v }
                                                    })}
                                                >
                                                    <SelectTrigger className="bg-[#0d2847] border-white/10">
                                                        <SelectValue placeholder="Select team" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                                        <SelectItem value={matchPlayers.home_team?.name || editingMatch.home_team}>
                                                            {matchPlayers.home_team?.name || editingMatch.home_team}
                                                        </SelectItem>
                                                        <SelectItem value={matchPlayers.away_team?.name || editingMatch.away_team}>
                                                            {matchPlayers.away_team?.name || editingMatch.away_team}
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                {getAllPlayers().filter(p => !statsForm.defensive_mvp?.team || p.team === statsForm.defensive_mvp.team).length > 0 ? (
                                                    <Select
                                                        value={statsForm.defensive_mvp?.player_name || undefined}
                                                        onValueChange={(v) => {
                                                            const player = getAllPlayers().find(p => p.name === v);
                                                            setStatsForm({
                                                                ...statsForm,
                                                                defensive_mvp: { ...statsForm.defensive_mvp, player_name: v, team: player?.team || statsForm.defensive_mvp?.team }
                                                            });
                                                        }}
                                                    >
                                                        <SelectTrigger className="bg-[#0d2847] border-white/10">
                                                            <SelectValue placeholder="Select player" />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-[#0d2847] border-white/10">
                                                            {getAllPlayers().filter(p => !statsForm.defensive_mvp?.team || p.team === statsForm.defensive_mvp.team).map((p, i) => (
                                                                <SelectItem key={i} value={p.name}>{p.displayName}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                ) : (
                                                    <Input
                                                        value={statsForm.defensive_mvp?.player_name || ''}
                                                        onChange={(e) => setStatsForm({
                                                            ...statsForm,
                                                            defensive_mvp: { ...statsForm.defensive_mvp, player_name: e.target.value }
                                                        })}
                                                        placeholder="Enter player name"
                                                        className="bg-[#0d2847] border-white/10"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </div>
                    )}
                    
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button variant="outline" onClick={() => setShowStatsDialog(false)} className="border-white/10">
                            Cancel
                        </Button>
                        <Button onClick={handleSaveStats} className="bg-primary hover:bg-cyan-400 text-[#0a1628]">
                            Save Statistics
                        </Button>
                        {editingMatch?.status !== 'completed' && (
                            <Button 
                                onClick={() => handleCompleteMatch(true)} 
                                className="bg-green-600 hover:bg-green-700"
                            >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Complete & Advance Winner
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MatchesTab;
